// Excel (.xlsx) export helper built on ExcelJS.
//
// ExcelJS is large, so it is loaded lazily — only when the user actually clicks
// an Export button — and never weighs down the initial bundle.
//
//   await exportToExcel({
//     fileName: "Attendance-2026-11-04.xlsx",
//     sheets: [{
//       name: "Daily",
//       title: "Daily Attendance — 04 Nov 2026",
//       subtitle: "Shift 10:00 – 19:00",
//       columns: [{ header: "Code", key: "code", width: 10 }, ...],
//       rows: [{ code: "7", ... }],
//       // optional: per-cell fill colour (hex without #) e.g. for status cells
//       cellFill: (row, column, value) => "DCFCE7" | null,
//       // optional: extra rows appended after the data (e.g. a legend)
//       footer: [["P = Present", "A = Absent"]],
//     }],
//   });

const HEADER_FILL = "1D4ED8";
const BORDER = { style: "thin", color: { argb: "FFE2E8F0" } };
const CELL_BORDER = { top: BORDER, left: BORDER, bottom: BORDER, right: BORDER };

async function loadExcel() {
  const mod = await import("exceljs");
  return mod.default || mod;
}

function download(buffer, fileName) {
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function exportToExcel({ fileName, sheets }) {
  const ExcelJS = await loadExcel();
  const wb = new ExcelJS.Workbook();
  wb.creator = "RABS Workspace";
  wb.created = new Date();

  for (const sheet of sheets) {
    const ws = wb.addWorksheet(sheet.name.slice(0, 31));
    const colCount = sheet.columns.length;
    let headerRowNo = 1;

    // Title + subtitle rows (merged across all columns)
    if (sheet.title) {
      ws.addRow([sheet.title]);
      ws.mergeCells(1, 1, 1, colCount);
      const c = ws.getCell(1, 1);
      c.font = { bold: true, size: 14, color: { argb: "FF0F172A" } };
      ws.getRow(1).height = 22;
      headerRowNo = 2;
      if (sheet.subtitle) {
        ws.addRow([sheet.subtitle]);
        ws.mergeCells(2, 1, 2, colCount);
        ws.getCell(2, 1).font = { size: 10, color: { argb: "FF64748B" } };
        headerRowNo = 3;
      }
      ws.addRow([]);
      headerRowNo += 1;
    }

    // Header row
    const header = ws.addRow(sheet.columns.map((c) => c.header));
    header.height = 20;
    header.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${HEADER_FILL}` } };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      cell.border = CELL_BORDER;
    });

    // Data rows
    for (const row of sheet.rows) {
      const values = sheet.columns.map((c) => row[c.key] ?? "");
      const r = ws.addRow(values);
      r.eachCell({ includeEmpty: true }, (cell, colNo) => {
        const col = sheet.columns[colNo - 1];
        cell.border = CELL_BORDER;
        cell.alignment = { vertical: "middle", horizontal: col?.align || "left" };
        const fill = sheet.cellFill?.(row, col, cell.value);
        if (fill) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${fill}` } };
        }
      });
    }

    // Column widths, frozen header, filter
    sheet.columns.forEach((c, i) => {
      ws.getColumn(i + 1).width = c.width || 14;
    });
    ws.views = [
      { state: "frozen", ySplit: headerRowNo, xSplit: sheet.freezeColumns || 0 },
    ];
    if (sheet.rows.length) {
      ws.autoFilter = {
        from: { row: headerRowNo, column: 1 },
        to: { row: headerRowNo + sheet.rows.length, column: colCount },
      };
    }

    // Optional footer (legend / notes)
    if (sheet.footer?.length) {
      ws.addRow([]);
      for (const line of sheet.footer) {
        const r = ws.addRow(line);
        r.font = { size: 10, color: { argb: "FF64748B" } };
      }
    }
  }

  const buffer = await wb.xlsx.writeBuffer();
  download(buffer, fileName);
}
